/** Typed client for the puzzle archive. */

import { requestJson, type RequestOptions } from "./httpClient";
import { MEMBER_CSRF_COOKIE } from "./accountsApi";

/** Matches the server's PUZZLE_CHARACTERS, which came from the club's own notes. */
export const PUZZLE_CHARACTERS = ["educational", "exercise", "demon", "fun"] as const;
export type PuzzleCharacter = (typeof PUZZLE_CHARACTERS)[number];

export const MIN_DIFFICULTY = 1;
export const MAX_DIFFICULTY = 10;
export const MIN_QUALITY = 1;
export const MAX_QUALITY = 5;

export interface RatingSummary {
  readonly count: number;
  readonly difficulty: number | null;
  readonly quality: number | null;
  readonly solved_rate: number | null;
  readonly character: PuzzleCharacter | null;
}

export interface MemberRating {
  readonly difficulty: number;
  readonly quality: number | null;
  readonly character: PuzzleCharacter | null;
  readonly solved: boolean | null;
}

export interface Puzzle {
  readonly id: number;
  readonly title: string;
  readonly creator: string | null;
  /** The spreadsheet's original vibe-based number, kept as history. */
  readonly archive_difficulty: number | null;
  readonly puzzle_url: string;
  readonly solution_url: string;
  readonly set_name: string | null;
  readonly added_on: string | null;
  readonly ratings: RatingSummary;
  readonly my_rating: MemberRating | null;
}

export interface PuzzleList {
  readonly puzzles: readonly Puzzle[];
  readonly sets: readonly string[];
  readonly signed_in: boolean;
}

export interface RatingDraft {
  readonly difficulty: number;
  readonly quality?: number | null;
  readonly character?: PuzzleCharacter | null;
  readonly solved?: boolean | null;
}

const request = <T,>(path: string, options?: RequestOptions) =>
  requestJson<T>("/api/puzzles", path, options, MEMBER_CSRF_COOKIE);

export const puzzlesApi = {
  list: (signal?: AbortSignal) => request<PuzzleList>("", { signal }),
  rate: (id: number, draft: RatingDraft) =>
    request<Puzzle>(`/${id}/rating`, { method: "PUT", body: draft }),
  unrate: (id: number) => request<Puzzle>(`/${id}/rating`, { method: "DELETE" }),
};
